import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
    CandidateProfileComponent,
    ProfileComponent,
    ProfileComponentState
} from 'src/app/recruiting/candidates/new-profile/store/profile-component.state';
import { ProfileComponentCategory } from 'src/app/shared/models/enums/profile-component-category.enum';
import { ProfileComponentType } from 'src/app/shared/models/enums/profile-component-type.enum';
import dayjs from 'dayjs';

export const profileComponentFeatureKey = 'profileComponent';
export const profileComponentFeature = createFeatureSelector<ProfileComponentState>(profileComponentFeatureKey);
export const selectIsAllowedToViewProfileAlerts = createSelector(
    profileComponentFeature,
    (state) => state?.allowProfileComponentsView
);

export const selectProfileComponents = createSelector(profileComponentFeature, (state) => state?.profileComponents);

export const selectProfileComponentsisLoading = createSelector(profileComponentFeature, (state) => state?.isLoading);

export const selectMaxEducationEntriesReached = createSelector(
    profileComponentFeature,
    (state) => state?.maxEducationEntriesReached
);

export const selectProfileComponentsByCategory = (category: number) =>
    createSelector(selectProfileComponents, (profileComponents: ProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponentCategory === category)
    );

export const selectAlertCountByCategory = (category: number) =>
    createSelector(
        selectProfileComponentsByCategory(category),
        (profileComponents: ProfileComponent) => profileComponents?.alertCount
    );

export const selectCandidateProfileComponentsByCategory = (category: number) =>
    createSelector(
        selectProfileComponentsByCategory(category),
        (profileComponents: ProfileComponent) => profileComponents?.candidateProfileComponents
    );

export const selectAboutHasAlert = createSelector(
    selectAlertCountByCategory(ProfileComponentCategory.About),
    (count) => count > 0
);

export const selectIsAddressVerified = createSelector(
    selectCandidateProfileComponentsByCategory(ProfileComponentCategory.About),
    (profileComponents: CandidateProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponent === ProfileComponentType.Address).isValid
);

export const selectIsAddressMissing = createSelector(
    selectCandidateProfileComponentsByCategory(ProfileComponentCategory.About),
    (profileComponents: CandidateProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponent === ProfileComponentType.AddressMissing).isValid === false
);

export const selectIsRTOValid = createSelector(
    selectCandidateProfileComponentsByCategory(ProfileComponentCategory.About),
    (profileComponents: CandidateProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponent === ProfileComponentType.RTO).isValid
);

export const selectRTOValidatedBy = createSelector(
    selectCandidateProfileComponentsByCategory(ProfileComponentCategory.About),
    (profileComponents: CandidateProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponent === ProfileComponentType.RTO).validatedBy
);

export const selectIsPrimaryPhoneValid = createSelector(
    selectCandidateProfileComponentsByCategory(ProfileComponentCategory.About),
    (profileComponents: CandidateProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponent === ProfileComponentType.PhoneNumber).isValid
);

export const selectIsLicenseVerified = createSelector(
    selectCandidateProfileComponentsByCategory(ProfileComponentCategory.Qualifications),
    (profileComponents: CandidateProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponent === ProfileComponentType.LicenseMissing)?.isValid
);

export const selectIsCertificationsVerified = createSelector(
    selectCandidateProfileComponentsByCategory(ProfileComponentCategory.Qualifications),
    (profileComponents: CandidateProfileComponent[]) =>
        profileComponents?.find((x) => x.profileComponent === ProfileComponentType.CertificationMissing)?.isValid
);

export const selectInvalidProfileComponents = createSelector(
    selectProfileComponents,
    (profileComponents: ProfileComponent[]) =>
        profileComponents
            ?.filter((p) => p.profileComponentCategory !== ProfileComponentCategory.MyAyaContact)
            .flatMap((p) => p.candidateProfileComponents)
            ?.filter((p) => !p.isValid)
);

export const selectCandidateProfileComponentsByProfileComponentType = (profileComponentTypes: ProfileComponentType[]) =>
    createSelector(selectProfileComponents, (profileComponents: ProfileComponent[]) =>
        profileComponents
            ?.flatMap((p) => p.candidateProfileComponents)
            ?.filter((p) => profileComponentTypes.includes(p.profileComponent))
    );

export const selectPhoneNumberComponentLastVerifiedMessage = createSelector(
    selectCandidateProfileComponentsByProfileComponentType([
        ProfileComponentType.PhoneNumber,
        ProfileComponentType.CandidatePhoneNumber
    ]),
    (profileComponents: CandidateProfileComponent[]) => {
        const phoneProfileComponents = profileComponents?.filter((x) => x.validatedOnDateUTC !== null);

        if (!phoneProfileComponents || phoneProfileComponents.length === 0) {
            return '';
        }

        // Selecting the most recent updated/verified component.
        phoneProfileComponents.sort((a, b) => dayjs(b.validatedOnDateUTC).diff(dayjs(a.validatedOnDateUTC)));
        const selectedComponent = phoneProfileComponents[0];

        const changeAuthor = selectedComponent.validatedBy.replace(/[^a-zA-Z ]/g, '');
        const changeDate = dayjs(selectedComponent.validatedOnDateUTC).format('MM/DD/YYYY');
        const updatedOrVerifiedText =
            selectedComponent.profileComponent === ProfileComponentType.PhoneNumber ? 'verified' : 'updated';

        return `Last ${updatedOrVerifiedText} by ${changeAuthor} on ${changeDate}`;
    }
);

export const selectAddressComponentLastVerifiedMessage = createSelector(
    selectCandidateProfileComponentsByProfileComponentType([
        ProfileComponentType.Address,
        ProfileComponentType.CandidateAddress
    ]),
    (profileComponents: CandidateProfileComponent[]) => {
        const addressProfileComponents = profileComponents?.filter((x) => x.validatedOnDateUTC !== null);

        if (!addressProfileComponents || addressProfileComponents.length === 0) {
            return '';
        }

        // Selecting the most recent updated/verified component.
        addressProfileComponents.sort((a, b) => dayjs(b.validatedOnDateUTC).diff(dayjs(a.validatedOnDateUTC)));
        const selectedComponent = addressProfileComponents[0];

        const changeAuthor = selectedComponent.validatedBy.replace(/[^a-zA-Z ]/g, '');
        const changeDate = dayjs(selectedComponent.validatedOnDateUTC).format('MM/DD/YYYY');
        const updatedOrVerifiedText =
            selectedComponent.profileComponent === ProfileComponentType.Address ? 'verified' : 'updated';

        return `Last ${updatedOrVerifiedText} by ${changeAuthor} on ${changeDate}`;
    }
);
